/**
 * Portfolio Tasks (PortfolioTaskCN) Redux slice — keyed by portfolioId so multiple
 * portfolios can be cached independently without overwriting each other.
 *
 * Mirrors the per-portfolio shape used by `portfolioFinancialsSlice`.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as portfolioTaskCNService from 'services/portfolioTaskCNService'
import type {
	PortfolioTask,
	PortfolioTaskCreateInput,
	PortfolioTaskUpdateInput,
} from 'types/portfolioTask'

const SLICE_NAME = 'portfolioTaskCN'

type PortfolioTaskCNState = {
	entitiesByPortfolioId: Record<string, PortfolioTask[]>
	loadingByPortfolioId: Record<string, boolean>
	errorByPortfolioId: Record<string, string | null>
}

const initialState: PortfolioTaskCNState = {
	entitiesByPortfolioId: {},
	loadingByPortfolioId: {},
	errorByPortfolioId: {},
}

const getToken = (state: any): string => state?.userLogin?.userInfo?.token

const errMsg = (err: any, fallback: string): string =>
	err?.response?.data?.message || err?.message || fallback

// ────── Thunks ─────────────────────────────────────────────────────────────

export const fetchPortfolioTasks = createAsyncThunk<
	{ portfolioId: string; tasks: PortfolioTask[] },
	string,
	{ rejectValue: { portfolioId: string; error: string } }
>(`${SLICE_NAME}/fetch`, async (portfolioId, { getState, rejectWithValue }) => {
	try {
		const token = getToken(getState())
		const tasks = await portfolioTaskCNService.getPortfolioTasks(portfolioId, token)
		return { portfolioId, tasks }
	} catch (err: any) {
		return rejectWithValue({
			portfolioId,
			error: errMsg(err, 'Failed to fetch portfolio tasks'),
		})
	}
})

export const createPortfolioTask = createAsyncThunk<
	{ portfolioId: string; task: PortfolioTask },
	PortfolioTaskCreateInput,
	{ rejectValue: { portfolioId: string; error: string } }
>(`${SLICE_NAME}/create`, async (input, { getState, rejectWithValue }) => {
	try {
		const token = getToken(getState())
		const task = await portfolioTaskCNService.createPortfolioTask(input, token)
		return { portfolioId: input.portfolioid, task }
	} catch (err: any) {
		return rejectWithValue({
			portfolioId: input.portfolioid,
			error: errMsg(err, 'Failed to create portfolio task'),
		})
	}
})

export const updatePortfolioTask = createAsyncThunk<
	{ portfolioId: string; task: PortfolioTask },
	{ taskId: string; portfolioId: string; patch: PortfolioTaskUpdateInput },
	{ rejectValue: { portfolioId: string; error: string } }
>(
	`${SLICE_NAME}/update`,
	async ({ taskId, portfolioId, patch }, { getState, rejectWithValue }) => {
		try {
			const token = getToken(getState())
			const task = await portfolioTaskCNService.updatePortfolioTask(taskId, patch, token)
			return { portfolioId, task }
		} catch (err: any) {
			return rejectWithValue({
				portfolioId,
				error: errMsg(err, 'Failed to update portfolio task'),
			})
		}
	},
)

export const deletePortfolioTask = createAsyncThunk<
	{ portfolioId: string; taskId: string },
	{ taskId: string; portfolioId: string },
	{ rejectValue: { portfolioId: string; error: string } }
>(
	`${SLICE_NAME}/delete`,
	async ({ taskId, portfolioId }, { getState, rejectWithValue }) => {
		try {
			const token = getToken(getState())
			await portfolioTaskCNService.deletePortfolioTask(taskId, token)
			return { portfolioId, taskId }
		} catch (err: any) {
			return rejectWithValue({
				portfolioId,
				error: errMsg(err, 'Failed to delete portfolio task'),
			})
		}
	},
)

// ────── Slice ──────────────────────────────────────────────────────────────

const portfolioTaskCNSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearPortfolioTasks(state, action: { payload: string }) {
			delete state.entitiesByPortfolioId[action.payload]
			delete state.loadingByPortfolioId[action.payload]
			delete state.errorByPortfolioId[action.payload]
		},
	},
	extraReducers: builder => {
		builder
			// fetch
			.addCase(fetchPortfolioTasks.pending, (state, action) => {
				const portfolioId = action.meta.arg
				state.loadingByPortfolioId[portfolioId] = true
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(fetchPortfolioTasks.fulfilled, (state, action) => {
				const { portfolioId, tasks } = action.payload
				state.entitiesByPortfolioId[portfolioId] = tasks
				state.loadingByPortfolioId[portfolioId] = false
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(fetchPortfolioTasks.rejected, (state, action) => {
				const portfolioId = action.payload?.portfolioId ?? action.meta.arg
				state.loadingByPortfolioId[portfolioId] = false
				state.errorByPortfolioId[portfolioId] =
					action.payload?.error || action.error?.message || 'Failed to fetch portfolio tasks'
			})

			// create
			.addCase(createPortfolioTask.fulfilled, (state, action) => {
				const { portfolioId, task } = action.payload
				const existing = state.entitiesByPortfolioId[portfolioId] || []
				state.entitiesByPortfolioId[portfolioId] = [task, ...existing]
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(createPortfolioTask.rejected, (state, action) => {
				const portfolioId = action.payload?.portfolioId ?? action.meta.arg.portfolioid
				state.errorByPortfolioId[portfolioId] =
					action.payload?.error || action.error?.message || 'Failed to create portfolio task'
			})

			// update
			.addCase(updatePortfolioTask.fulfilled, (state, action) => {
				const { portfolioId, task } = action.payload
				const existing = state.entitiesByPortfolioId[portfolioId] || []
				state.entitiesByPortfolioId[portfolioId] = existing.map(t =>
					t._id === task._id ? task : t,
				)
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(updatePortfolioTask.rejected, (state, action) => {
				const portfolioId = action.payload?.portfolioId ?? action.meta.arg.portfolioId
				state.errorByPortfolioId[portfolioId] =
					action.payload?.error || action.error?.message || 'Failed to update portfolio task'
			})

			// delete
			.addCase(deletePortfolioTask.fulfilled, (state, action) => {
				const { portfolioId, taskId } = action.payload
				const existing = state.entitiesByPortfolioId[portfolioId] || []
				state.entitiesByPortfolioId[portfolioId] = existing.filter(t => t._id !== taskId)
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(deletePortfolioTask.rejected, (state, action) => {
				const portfolioId = action.payload?.portfolioId ?? action.meta.arg.portfolioId
				state.errorByPortfolioId[portfolioId] =
					action.payload?.error || action.error?.message || 'Failed to delete portfolio task'
			})
	},
})

export const { clearPortfolioTasks } = portfolioTaskCNSlice.actions

// ────── Selectors ──────────────────────────────────────────────────────────

export const selectPortfolioTasks = (
	state: any,
	portfolioId: string,
): PortfolioTask[] => state.portfolioTaskCN?.entitiesByPortfolioId?.[portfolioId] ?? []

export const selectPortfolioTasksLoading = (state: any, portfolioId: string): boolean =>
	!!state.portfolioTaskCN?.loadingByPortfolioId?.[portfolioId]

export const selectPortfolioTasksError = (
	state: any,
	portfolioId: string,
): string | null => state.portfolioTaskCN?.errorByPortfolioId?.[portfolioId] ?? null

export default portfolioTaskCNSlice
